import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  ArrowRightIcon,
  UserPlusIcon,
} from "@heroicons/react/20/solid";
import { searchTripsByMrn, fetchCooperateDetails } from "services/memberMrn";
import { ToastError } from "utils/toasts";

export default function FundingSourcePage() {
  const location = useLocation();
  const [memberId, setMemberId] = useState(location.state?.prefillMrn || "");
  const [notFoundId, setNotFoundId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [fundingSource, setFundingSource] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  const navigate = useNavigate();
  const { fundingsourceId } = useParams();

  useEffect(() => {
    if (!fundingsourceId) {
      setShowSplash(false);
      return;
    }

    let cancelled = false;
    const minDelay = new Promise((resolve) => setTimeout(resolve, 3000));

    fetchCooperateDetails(fundingsourceId)
      .then((data) => {
        if (!cancelled) setFundingSource(data);
      })
      .catch((error) => {
        if (!cancelled) {
          ToastError(error.message || "Unable to fetch funding source details");
        }
      })
      .finally(() => {
        minDelay.then(() => {
          if (!cancelled) setShowSplash(false);
        });
      });

    return () => {
      cancelled = true;
    };
  }, [fundingsourceId]);

  async function handleSearch(e) {
    e.preventDefault();
    const id = memberId.trim();
    if (!id || searching) return;

    setSearching(true);
    try {
      const result = await searchTripsByMrn(id, fundingsourceId);
      if (result === null) {
        setNotFoundId(id);
      } else {
        navigate(`/fundingsource/${fundingsourceId}/trips/${id}`, {
          state: { fundingSource },
        });
      }
    } catch (error) {
      ToastError(error.message || "Unable to search for member");
    } finally {
      setSearching(false);
    }
  }

  function handleCreateMember() {
    navigate(`/fundingsource/${fundingsourceId}/create-member/${notFoundId}`, {
      state: { fundingSource },
    });
  }

  function dismissNotFound() {
    setNotFoundId(null);
  }

  const fundingSourceName =
    fundingSource?.displayName ||
    fundingSource?.cooperateName ||
    fundingSource?.companyName ||
    "";

  const fundingSourceAvatar = fundingSource && (
    <>
      {fundingSource.profileImageURL ? (
        <img
          src={fundingSource.profileImageURL}
          alt={fundingSourceName || "Logo"}
          className="h-full w-full object-contain rounded-[inherit] bg-white p-1"
        />
      ) : (
        <span className="font-bold">
          {(fundingSourceName || "F").charAt(0).toUpperCase()}
        </span>
      )}
    </>
  );

  const searchPlaceholder = "Enter Member MRN/Phone Number";

  if (showSplash) {
    return (
      <section className="relative min-h-dvh overflow-hidden flex items-center justify-center p-4">
        <video
          autoPlay
          muted
          loop
          playsInline
          poster="https://assets.nemtsoftware.com/hero.jpg"
          className="absolute inset-0 h-full w-full object-cover"
          src="https://assets.nemtsoftware.com/generic-video-transportation.mp4"
        />
        <div className="absolute inset-0 bg-white/80" />
        <div className="relative flex flex-col items-center gap-4">
          <div className="h-24 w-24 rounded-3xl bg-white shadow-[0_10px_40px_rgb(0,0,0,0.08)] ring-1 ring-black/5 flex items-center justify-center overflow-hidden">
            {fundingSource?.profileImageURL ? (
              <img
                src={fundingSource.profileImageURL}
                alt={fundingSourceName || "Logo"}
                className="h-full w-full object-contain p-3"
              />
            ) : fundingSourceName ? (
              <span className="text-3xl font-bold text-[#293562]">
                {fundingSourceName.charAt(0).toUpperCase()}
              </span>
            ) : (
              <div className="h-8 w-8 rounded-full border-2 border-[#293562]/20 border-t-[#293562] animate-spin" />
            )}
          </div>
          {fundingSourceName && (
            <p className="text-[15px] font-semibold text-gray-900">
              {fundingSourceName}
            </p>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-dvh overflow-hidden flex items-center justify-center p-4">
      <video
        autoPlay
        muted
        loop
        playsInline
        poster="https://assets.nemtsoftware.com/hero.jpg"
        className="absolute inset-0 h-full w-full object-cover"
        src="https://assets.nemtsoftware.com/generic-video-transportation.mp4"
      />
      <div className="absolute inset-0 bg-white/80" />
      <div className="relative w-full max-w-lg space-y-6">
        <div className="rounded-[28px] bg-white/80 backdrop-blur-2xl ring-1 ring-black/5 shadow-[0_10px_40px_rgb(0,0,0,0.06)] px-6 py-6 space-y-5">
          {fundingSource && (
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 shrink-0 rounded-full bg-gradient-to-br from-[#E0E3F2] to-[#cdd3ec] flex items-center justify-center text-[#293562] text-sm overflow-hidden ring-1 ring-black/5">
                {fundingSourceAvatar}
              </div>
              <div className="min-w-0">
                <p className="text-[15px] font-semibold text-gray-900 truncate">
                  {fundingSourceName}
                </p>
                {/* <p className="text-[12px] text-gray-400 truncate">
                  {[fundingSource.email, fundingSource.contactNumber]
                    .filter(Boolean)
                    .join("  ·  ")}
                </p> */}
              </div>
            </div>
          )}

          <h2
            className={`text-[21px] font-bold text-gray-900 tracking-tight ${
              fundingSource ? "pt-1 border-t border-gray-100 mt-1" : ""
            }`}
          >
            Enter Member MRN/Phone Number
          </h2>

          <form onSubmit={handleSearch}>
            <div className="flex items-center gap-2.5 bg-gray-100 rounded-2xl px-4 py-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 shrink-0" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={memberId}
                onChange={(e) => {
                  setMemberId(e.target.value);
                  if (notFoundId) setNotFoundId(null);
                }}
                autoFocus
                disabled={searching}
                className="flex-1 bg-transparent border-0 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={!memberId.trim() || searching}
                className="h-9 w-9 shrink-0 flex items-center justify-center rounded-full bg-[#293562] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:brightness-95 transition"
                aria-label="Search"
              >
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>

        {notFoundId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
            <div className="w-full max-w-xs bg-white/95 backdrop-blur-xl rounded-3xl shadow-[0_20px_60px_rgb(0,0,0,0.2)] overflow-hidden">
              <div className="px-6 pt-7 pb-5 text-center space-y-2">
                <div className="mx-auto h-12 w-12 rounded-2xl bg-[#E0E3F2] flex items-center justify-center">
                  <UserPlusIcon className="h-6 w-6 text-[#293562]" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  Member Not Found
                </h3>
                <p className="text-sm text-gray-500">
                  No member exists with ID {notFoundId}. Would you like to
                  create one?
                </p>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200">
                <button
                  type="button"
                  onClick={dismissNotFound}
                  className="py-3.5 text-sm font-medium text-gray-500 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateMember}
                  className="py-3.5 text-sm font-semibold text-blue-500 hover:bg-gray-50 transition"
                >
                  Create Member
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
